import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/services/auth/authentication.service';
import { Title } from '@angular/platform-browser';
import { ArbolService } from '@app/services/arbol/arbol.service';
import { StatusService } from '@app/services/status';
import { EmpresaService } from '@app/modules/empresa/services/empresa.service';
import { ToastrService } from 'ngx-toastr';
import { StorageService } from '@app/services/storage';
import { EmpresaCentralizadaService } from '@app/services/empresaCentralizada/empresaCentralizada.service';
import { Subject, takeUntil } from 'rxjs';

@Component({ standalone: false, selector: 'app-sso',
  templateUrl: './sso.component.html',
  styleUrls: ['./sso.component.scss'],
})
export class SsoComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  public loading: boolean;
  public error: boolean;

  constructor(
    private readonly empresaService: EmpresaService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authService: AuthenticationService,
    private readonly titleService: Title,
    private readonly arbolService: ArbolService,
    private readonly stateService: StatusService,
    private readonly toastr: ToastrService,
    private readonly storageService: StorageService,
    private readonly empresaCentralizadaService: EmpresaCentralizadaService
  ) {
    this.loading = true;
  }

  ngOnInit() {
    this.loading = true;
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      this.authService.clear();
      try {
        const decoded = JSON.parse(window.atob(response.token));
        const token = decoded.token;
        const dlToken = decoded?.dlToken ?? '';
        const action = decoded.action;
        const payload = decoded.payload;

        if (token) {
          localStorage.setItem('ambito_id', this.authService.parseJWT(token).ambito);
          localStorage.setItem('selected_language', this.authService.parseJWT(token).lang);
          localStorage.setItem('access_token', token);
          localStorage.setItem('dlToken', dlToken);
          localStorage.setItem('gw2Token', token);
          localStorage.setItem('dominio', this.authService.parseJWT(token).dominio);

          this.authService.getConfigurations().subscribe({ next: (response: any) => {
              this.arbolService.getGrupos().then((r: any) => {
                if (action?.empresa_id) {
                  const shouldRoute = this.selectEmpresaForAction(r, action);
                  localStorage.setItem('permisos', JSON.stringify(response['Permiso']));
                  localStorage.setItem('current_user', JSON.stringify(response['userInfo']));
                  localStorage.setItem('configuraciones', JSON.stringify(response['Configuracion']));
                  this.authService.isLoggedIn = true;
                  if (shouldRoute) {
                    if (payload) {
                      this.routeSucuralByAction(action, payload);
                    } else {
                      this.routeByAction(action);
                    }
                  } else {
                    this.router.navigate(['dashboard']);
                  }
                } else {
                  localStorage.setItem('permisos', JSON.stringify(response['Permiso']));
                  localStorage.setItem('current_user', JSON.stringify(response['userInfo']));
                  localStorage.setItem('configuraciones', JSON.stringify(response['Configuracion']));
                  this.authService.isLoggedIn = true;
                  this.router.navigate(['dashboard']);
                }
              }, () => {
                this.loading = false;
                this.authService.isLoggedIn = false;
                localStorage.clear();
                this.error = true;
              });
            }, error: (response: any) => {
              this.loading = false;
              localStorage.clear();
              this.error = true;
            }
          });
        } else {
          this.loading = false;
          this.error = true;
          localStorage.clear();
          this.toastr.info('No se ha proporcionado token de acceso');
        }
      } catch (ex) {
        this.loading = false;
        localStorage.clear();
        this.error = true;
      }
    });
  }

  private selectEmpresaForAction(grupos: any[], action: any): boolean {
    for (const grupo of grupos) {
      const empresa = grupo.children?.find(item => item.empresa_id === action.empresa_id);
      if (empresa) {
        action.grupo = grupo.codigo;
        this.empresaService.set(grupo.codigo, action.empresa_id, empresa.server_data);
        return true;
      }
      if (grupo.children === undefined) {
        action.grupo = grupo.grupo_id;
        this.empresaService.set(action.grupo, action.empresa_id, grupo.server_data);
        return true;
      }
    }
    return false;
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  routeByAction(action: any) {
    if (action.grupo && action.empresa_id) {
      this.empresaCentralizadaService.setEmpresaSeleccionadaId(action.empresa_id);
      this.storageService.setEmpresa(action.empresa_id, ''); // El flujo SSO no proporciona el nombre de la empresa.
      this.storageService.setGrupoId(action.grupo);
      const rutas: Record<string, any[]> = {
        motivo_compra: ['mostrar', 'motivocompra'],
        motivo_visita: ['mostrar', 'motivovisita'],
        mutuas: ['mostrar', 'mutuas'],
        proveedores: ['mostrar', 'fabricantes'],
        'configuracion.tiendas.formas_pago': ['mostrar', 'tienda', 'formaspago'],
        'configuracion.tiendas.tipos_venta': ['mostrar', 'tienda', 'tiposdeventa'],
        'configuracion.tipos_impuestos': ['mostrar', 'tienda', 'tiposimpuesto'],
        'configuracion.mutuas.editar_copagos': ['mostrar', 'mutuas', 'copagos'],
        'configuracion.gabinete.doctores': ['mostrar', 'gabinete', 'doctores'],
        'configuracion.gabinete.audio_prescriptores': ['mostrar', 'gabinete', 'prescriptores'],
        'configuracion.clientes.grupos_clientes': ['mostrar', 'clientes', 'gruposclientes'],
        'configuracion.clientes.no_screening': ['mostrar', 'clientes', 'motivoscreening'],
        'configuracion.clientes.profesiones': ['mostrar', 'clientes', 'profesiones'],
        'configuracion.compras.series': ['mostrar', 'series'],
        'configuracion.almacen.tipos_ajuste': ['mostrar', 'almacen', 'tiposajuste'],
        'configuracion.marketing.tipos_aviso': ['mostrar', 'marketing', 'tiposaviso'],
        'configuracion.agenda.tipos_visita': ['mostrar', 'agenda', 'tiposvisita'],
        'configuracion.empresa.bonificaciones': ['mostrar', 'tienda', 'bonificaciones'],
        'herramientas.controlhorario': ['mostrar', 'plan', 'advanceplan'],
        'configuracion.clientes.promociones': ['mostrar', 'clientes', 'promociones'],
        'configuracion.sociedad.editar': ['mostrar', 'empresa', 'sociedades', action.sociedad_id],
        'configuracion.tienda.pie.cabecera.factura': ['sucursal', action.sucursal, 'configurar-pie-cabecera-facturas']
      };
      const ruta = rutas[action.action];
      ruta ? this.navegarDashboard(action, ...ruta) : this.router.navigate(['dashboard']);
    }
  }

  private navegarDashboard(action: any, ...ruta: any[]): void {
    this.router.navigate(['dashboard', 'grupo', action.grupo, 'empresa', action.empresa_id, ...ruta]);
  }

  // Cuando es a nivel de sucursal.
  routeSucuralByAction(action: any, payload: any) {
    if (action.grupo && action.empresa_id) {
      const tienda = payload.sucursal;
      this.storageService.setSucursalId(tienda);
      this.storageService.setEmpresa(action.empresa_id, ''); // El flujo SSO no proporciona el nombre de la empresa.
      this.storageService.setGrupoId(action.grupo);
      this.arbolService.getSucursales(action.empresa_id).then((r) => {
        if (action.action === 'configuracion.tienda.altatienda.rgpd') {
          this.router.navigate(['dashboard', 'grupo', action.grupo, 'empresa', action.empresa_id, 'mostrar', 'tienda', 'altatienda', 'rpgd'], { queryParams: { tienda } });
        } else {
          this.router.navigate(['dashboard']);
        }
      });
    }
  }
}
